import { useState } from "react";
import { Play } from "lucide-react";
import approvalVideo from "@/assets/approval-video.mp4.asset.json";

type Props = {
  heading?: string;
  eyebrow?: string;
};

export function FeaturedAchievements({
  heading = "Don't just take our word for it. Watch our students win.",
  eyebrow = "Featured Achievements",
}: Props) {
  const [loadVideo, setLoadVideo] = useState(false);
  return (
    <section className="bg-accent/30 py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">
            {eyebrow}
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            {heading}
          </h2>
        </div>

        <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 md:items-stretch">
          {/* Video - click-to-load so the 9 MB MP4 isn't fetched on initial page load */}
          <div className="rounded-3xl overflow-hidden shadow-soft border border-border bg-black h-full flex aspect-video md:aspect-auto">
            {loadVideo ? (
              <video
                src={approvalVideo.url}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="w-full h-full object-contain bg-black"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <button
                type="button"
                onClick={() => setLoadVideo(true)}
                aria-label="Play approval highlights video"
                className="group relative w-full h-full min-h-[220px] grid place-items-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-black text-white"
              >
                <div className="flex flex-col items-center gap-3">
                  <span className="h-16 w-16 rounded-full bg-white/95 text-primary grid place-items-center shadow-2xl transition group-hover:scale-110">
                    <Play className="h-7 w-7 fill-current ml-1" />
                  </span>
                  <span className="text-sm font-medium tracking-wide opacity-90">
                    Tap to play approval highlights
                  </span>
                </div>
              </button>
            )}
          </div>


          {/* LinkedIn post text */}
          <article className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-soft h-full">
            <div className="flex items-center gap-3 pb-4 border-b border-border">
              <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center text-primary font-display font-semibold">
                W
              </div>
              <div>
                <a
                  href="https://in.linkedin.com/company/welivdit"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-foreground hover:underline"
                >
                  WeLivdIt
                </a>
                <div className="text-xs text-muted-foreground">on LinkedIn</div>
              </div>
            </div>
            <div className="mt-5 space-y-3 text-sm md:text-[15px] leading-relaxed text-foreground/85">
              <p className="font-semibold text-foreground">
                100 approvals. 100 different stories. 100 moments where preparation met opportunity. 🇮🇳
              </p>
              <p>
                Behind every approval was a student who had doubts, fears, questions, and uncertainty.
                Different universities. Different profiles. Different consulates. But one thing remained the same:
                <span className="font-medium text-foreground"> They showed up prepared.</span>
              </p>
              <p className="font-semibold text-foreground">And WeLivdIt stood behind them.</p>
              <p>
                These are real experiences shared by students from across India who trusted the WeLivdIt process and put in the work.
              </p>
              <p>If you're preparing for your visa interview, remember:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Confidence isn't built the night before the interview.</li>
                <li>It's built through clarity, practice, and preparation.</li>
              </ul>
              <p>
                📩 DM WeLivdIt to discuss your profile, mock interviews, DS-160, or visa strategy. Your approval story could be next.
              </p>
              <p className="text-primary text-xs font-medium pt-2">
                #WeLivdIt #F1Visa #VisaInterview #USVisa #StudyAbroad
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
